import React, { useMemo, useState, useEffect } from 'react';
import { AuxTypeCode, LightTypeMode } from '@/types/device';
import { MotorStatus, isMotorRunning } from '@/types/motorStatus';
import { MainPumpSection, SpaSection, OtherDevicesSection } from '@/components/devices';
import { FiSliders } from 'react-icons/fi';
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { apiService } from '@/utils/api';
import LightControlSidebar from '@/components/devices/LightControlSidebar';
import { Manual, Pump, ScheduleInfo, PoolLightSwitchMode, Aux, Dashboard, AuxAssign } from '@/types/dashboard';

import { AuxWithSchedules } from '@/types/dashboard';
import { useTimer } from '@/hooks/useTimer';
import { GetAuxInfoResponse, GetPoolLightSwitchModeResponse, GetPumpInfoResponse, ManualResponse } from '@/types/api';
import { useDispatch } from 'react-redux';
import { setLoading } from '@/store/slices/appSlice';

interface DeviceOverviewResponse {
  manual: Manual;
  poolLightSwitchMode: PoolLightSwitchMode;
  pump: Pump;
  scheduleInfo: ScheduleInfo;
  dashboard:Dashboard;
  auxAssign?: AuxAssign;
}

const getAuxKey = (aux: Pick<Aux, 'aux_type_code' | 'aux_slot_num'>): string =>
  `${aux.aux_type_code}-${aux.aux_slot_num}`;

const mergeAuxTimerFields = (current: Aux, latest?: Aux): Aux => {
  if (!latest) {
    return current;
  }

  return {
    ...current,
    aux_timer: latest.aux_timer,
    aux_timer_remain_sec: latest.aux_timer_remain_sec,
  };
};

const mergeAuxListTimerFields = (currentList: Aux[], latestList: Aux[] = []): Aux[] => {
  const latestMap = new Map(latestList.map((aux) => [getAuxKey(aux), aux]));

  return currentList.map((current) => mergeAuxTimerFields(current, latestMap.get(getAuxKey(current))));
};

const mergeAuxWithSchedulesTimerFields = (
  currentList: AuxWithSchedules[],
  latestList: Aux[] = [],
): AuxWithSchedules[] => {
  const latestMap = new Map(latestList.map((aux) => [getAuxKey(aux), aux]));

  return currentList.map((current) => ({
    ...current,
    aux: mergeAuxTimerFields(current.aux, latestMap.get(getAuxKey(current.aux))),
  }));
};

const DevicesPage: React.FC = () => {
  // const [loading, setLoading] = useState(false);
  const {selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();
  const [pumpInfo, setPumpInfo] = useState<Pump>({} as Pump);
  const [scheduleInfo, setScheduleInfo] = useState<ScheduleInfo | undefined>(undefined);
  const [spaList, setSpaList] = useState<AuxWithSchedules[]>([]);
  const [spaDevices, setSpaDevices] = useState<AuxWithSchedules[]>([]);
  const [pumpLinkedDeviceList, setPumpLinkedDeviceList] = useState<Aux[]>([]);
  const [pumpAux, setPumpAux] = useState<Aux>({} as Aux);
  const [devicesList, setDevicesList] = useState<AuxWithSchedules[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard>({} as Dashboard);
  const [hasRs485, setHasRs485] = useState(false);
  const [poolLightSwitchMode, setPoolLightSwitchMode] = useState<PoolLightSwitchMode>({} as PoolLightSwitchMode);
  const [isLightSidebarOpen, setIsLightSidebarOpen] = useState(false);
  const [selectedLightDevice, setSelectedLightDevice] = useState<AuxWithSchedules | null>(null);
  // 互斥状态标记：当 Cleaner 开启时，Spa 不可操作；当 Spa 开启时，Cleaner 不可操作
  const isCleanerOn = pumpLinkedDeviceList.some(
    (d) =>
      (d.aux_type_code === AuxTypeCode.POOL_PRESSURE_CLEANER ||
        d.aux_type_code === AuxTypeCode.POOL_IN_FLOOR_CLEANING) &&
      d.aux_status
  );
  // Spa 开启状态：包含 spaList 与 spaDevices 两类设备
  const isSpaOn = spaList.some((s) => !!s.aux?.aux_status) || spaDevices.some((s) => !!s.aux?.aux_status);

  const dispatch = useDispatch();
  // 加载设备数据
  const loadDeviceData = async () => {
    if (!selectedDeviceId) return;
    
    dispatch(setLoading(true));
    try {
      const response = await apiService.get<DeviceOverviewResponse>(`/api/v1/iot-shadow/device/${selectedDeviceId}/devicesOverview`);
      
      // 合并 SPA 和 SPA 设备数据
      const spa = Array.isArray(response.manual?.spa) ? response.manual.spa : []
      const spaDevices = Array.isArray(response.manual?.spa_devices) ? response.manual.spa_devices : []
      // 找到pump的aux
      const pumpAux = response.manual.aux.find(aux => aux.aux_type_code === AuxTypeCode.SPALSHME_PUMP);
      
      setPumpAux(pumpAux || {} as Aux);
      setScheduleInfo(response.scheduleInfo);
      setPumpInfo(response.pump);
      setPumpLinkedDeviceList(Array.isArray(response.manual?.pump_linked_devices) ? response.manual.pump_linked_devices : []);
      setPoolLightSwitchMode(response.poolLightSwitchMode);
      setHasRs485(!!response.auxAssign?.has_ecoclear);
      
      const getSchedulesForAux = (aux: Aux) => {
        const schedules = response.scheduleInfo?.schedules || [];
        return schedules.filter(
          (s) =>
            s.aux_type_code === aux.aux_type_code &&
            ((s.aux_slot ?? s.slot_num) === aux.aux_slot_num)
        );
      };

      // 为每个 spaList 中的 Aux 增加对应的 schedule 数组
      const spaWithSchedules = spa.map(spa => {
        const deviceSchedules = getSchedulesForAux(spa);
        const auxWithSchedules: AuxWithSchedules = {
          aux: spa,
          schedules: deviceSchedules
        };
        return auxWithSchedules;
      });

      const spaDevicesWithSchedules = spaDevices.map(spa => {
        const deviceSchedules = getSchedulesForAux(spa);
        const auxWithSchedules: AuxWithSchedules = {
          aux: spa,
          schedules: deviceSchedules
        };
        return auxWithSchedules;
      });

      const otherDevicesWithSchedules = (Array.isArray(response.manual?.other_devices) ? response.manual.other_devices : []).map(other => {
        const deviceSchedules = getSchedulesForAux(other);
        const auxWithSchedules: AuxWithSchedules = {
          aux: other,
          schedules: deviceSchedules
        };
        return auxWithSchedules;
      });
      
      setSpaList(spaWithSchedules);
      setSpaDevices(spaDevicesWithSchedules);
      setDevicesList(otherDevicesWithSchedules);
      setDashboard(response.dashboard);
    } catch (error) {
      console.error('Error loading device data:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // 当选中的设备变化时重新加载数据
  useEffect(() => {
    loadDeviceData();
    // 切换设备时复位等待定时器，并启动常规轮询
    pumpStatusTimer.stop();
    setExpectMotoStatus(null);
    pumpInfoPollTimer.reset();
    pumpInfoPollTimer.start();
    auxTimerPollTimer.reset();
    auxTimerPollTimer.start();
  }, [selectedDeviceId]);

  

  const [expectMotoStatus, setExpectMotoStatus] = useState<MotorStatus | null>(null);
  const pumpIsOn = useMemo(() => {
    if (expectMotoStatus) {
      return expectMotoStatus === MotorStatus.MOTOR_STATUS_RUNNING;
    }
    return isMotorRunning(pumpInfo?.motor_status ?? MotorStatus.MOTOR_STATUS_NOT_READY);
  }, [expectMotoStatus, pumpInfo?.motor_status]);

  // 定时获取泵信息的轮询定时器（与 pumpStatusTimer 互斥）
  const pumpInfoPollTimer = useTimer(
    async () => {
      await fetchPumpInfo();
    },
    {
      interval: 3000,
    }
  );

  const auxTimerPollTimer = useTimer(
    async () => {
      await fetchAuxTimerInfo();
    },
    {
      interval: 3000,
    }
  );

  const pumpStatusTimer = useTimer(
    async () => {
      if (!expectMotoStatus) return;
      try {
        const response = await apiService.get<GetPumpInfoResponse>(
          `/api/v1/iot-shadow/device/${selectedDeviceId}/pumpInfo`
        );
        console.log("fetchPumpInfo response:", response);
        setPumpInfo((prevPumpInfo) => ({
          ...prevPumpInfo,
          ...response.pump,
        }));
        console.log(
          "response.pump.motor_status:",
          response.pump.motor_status,
          ",expectMotoStatus:",
          expectMotoStatus
        );
        if (response.pump.motor_status === expectMotoStatus) {
          pumpStatusTimer.stop();
          dispatch(setLoading(false));
          setExpectMotoStatus(null);
          // 主泵达到预期状态后，恢复常规轮询
          pumpInfoPollTimer.start();
        }
      } catch (error) {
        console.error("Failed to fetch pump info:", error);
      }
    },
    {
      interval: 3000,
      maxCount: 10,
      onComplete: () => {
        dispatch(setLoading(false));
        setExpectMotoStatus(null);
        // 超时完成后恢复常规轮询
        pumpInfoPollTimer.start();
      },
    }
  );

  const setSpeedUI = (speed: number) => {
    setPumpAux((prevPumpAux) => ({
      ...prevPumpAux,
      pump_speed: speed,
    }));
  };

  const handlePumpToggle = async (isOn: boolean) => {
    dispatch(setLoading(true));
    setDashboardMainStatus(isOn);

    console.log("handlePumpToggle ", isOn);
   // 当 Spa 开启时，禁止用户改动主泵（与 Android 一致：Spa 模式下主泵开关不可修改）
    const spaCurrentlyOn = spaList.some((spa) => !!spa.aux?.aux_status);
    if (spaCurrentlyOn && !isOn) {
      console.log('[handlePumpToggle] 阻止在 Spa 模式下关闭主泵');
      // 直接恢复 UI 中的泵状态，不执行任何切换
      setDashboardMainStatus(true);
      dispatch(setLoading(false));
      return;
    }

    try {
       const newExpectMotoStatus = isOn
         ? MotorStatus.MOTOR_STATUS_RUNNING
         : MotorStatus.MOTOR_STATUS_STOPPED;
       setExpectMotoStatus(newExpectMotoStatus);
       // a) pump 关闭时，关闭所有 link 设备以及 spa 设备，并关闭 spa
       if (!isOn) {
         // 依次关闭联动设备（与 Android 中分步延迟一致）
         const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

         for (let i = 0; i < pumpLinkedDeviceList.length; i++) {
           const device = pumpLinkedDeviceList[i];
           try {
             await changeNormalEquipmentStatus(device.aux_type_code, device.aux_slot_num, false);
             setPumpLinkedDeviceStatus(device.aux_type_code, device.aux_slot_num, false);
             await sleep(300);
           } catch (err) {
             console.error('Failed to turn off linked device when pump off:', err);
           }
         }

         // 关闭 Spa 模式（仅对主控条目发一次请求）
         const primarySpa =
           spaList.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_RETURN) ||
           spaList.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_SUCTION) ||
           spaList[0];
         if (primarySpa) {
           try {
             await changeNormalEquipmentStatus(primarySpa.aux.aux_type_code, primarySpa.aux.aux_slot_num, false);
             setSpaStatus(primarySpa.aux.aux_type_code, primarySpa.aux.aux_slot_num, false);
             await sleep(100);
           } catch (err) {
             console.error('Failed to turn off spa when pump off:', err);
           }
         }
       }

       await changeMainPumpStatus(isOn);
      // 启动状态等待前停止常规轮询，避免并发
      pumpInfoPollTimer.stop();
      pumpStatusTimer.reset();
      pumpStatusTimer.start();
    } catch (error) {
      console.error("Failed to toggle pump:", error);
      setDashboardMainStatus(!isOn);
      setExpectMotoStatus(null);
      dispatch(setLoading(false));
    } 
  };

  const handleSpaToggle = async (typeCode: number, slotNum: number, IsOn: boolean) => {
    console.log('[handleSpaToggle] 开始切换状态:', { typeCode, slotNum, IsOn });
    console.log('[handleSpaToggle] 当前 spaList:', spaList);
    
    dispatch(setLoading(true));

    try {
      // c) Cleaner 和 Spa 互斥：如果 Cleaner 开着，则禁止开启 Spa；如果开启 Cleaner，要关闭 Spa
      const cleanerOn = pumpLinkedDeviceList.some(
        (d) =>
          (d.aux_type_code === AuxTypeCode.POOL_PRESSURE_CLEANER ||
            d.aux_type_code === AuxTypeCode.POOL_IN_FLOOR_CLEANING) &&
          d.aux_status
      );
      if (cleanerOn && IsOn) {
        console.log('[handleSpaToggle] Cleaner 正在运行，禁止开启 Spa');
        dispatch(setLoading(false));
        return;
      }

      setSpaStatus(typeCode, slotNum, IsOn);
      console.log('[handleSpaToggle] setSpaStatus 后的 spaList:', spaList);

      // 先发送当前设备的请求
      await changeNormalEquipmentStatus(typeCode, slotNum, IsOn);
      // 更新设备状态
      // 如果是开启 spa，需要关闭相关的清洁设备
      if (IsOn) {
        // b) 如果 pump 没开，则先把 pump 打开（与 Android 一致）
        if (!pumpIsOn) {
          try {
            setDashboardMainStatus(true);
            setExpectMotoStatus(MotorStatus.MOTOR_STATUS_RUNNING);
            await changeMainPumpStatus(true);
            // 启动状态等待前停止常规轮询，避免并发
            pumpInfoPollTimer.stop();
            pumpStatusTimer.reset();
            pumpStatusTimer.start();
          } catch (err) {
            console.error('Failed to turn on pump before enabling spa:', err);
            setExpectMotoStatus(null);
          }
        }

        // 延迟 100ms 后关闭清洁设备
        setTimeout(async () => {
          for (const device of pumpLinkedDeviceList) {
            if (
              device.aux_type_code === AuxTypeCode.POOL_PRESSURE_CLEANER ||
              device.aux_type_code === AuxTypeCode.POOL_IN_FLOOR_CLEANING
            ) {
              try {
                await changeNormalEquipmentStatus(
                  device.aux_type_code,
                  device.aux_slot_num,
                  false
                );
                // 更新设备状态
                setPumpLinkedDeviceStatus(device.aux_type_code, device.aux_slot_num, false);
              } catch (error) {
                console.error("Failed to turn off cleaner device:", error);
              }
            }
          }
        }, 100);
      } else {
        // 关闭 Spa 时恢复泵的可修改性（泵 UI 的不可修改通过 handler gate 实现）
      }
    } catch (error) {
      console.error("Failed to toggle spa:", error);
    } finally {
      setTimeout(async () => {
        await fetchManualDevice();
        await fetchPumpInfo()
        dispatch(setLoading(false));
      }, 3000);   
    }
  };

  const handleSpaDeviceToggle = async (typeCode: number, slotNum: number, IsOn: boolean) => {
    dispatch(setLoading(true));

    try {
      setSpaDeviceStatus(typeCode, slotNum, IsOn);
      await changeNormalEquipmentStatus(typeCode, slotNum, IsOn);

    } catch (error) {
      console.error("Failed to toggle spa device:", error);
    } finally {
      setTimeout(async () => {
        await fetchManualDevice();
        dispatch(setLoading(false));
      }, 3000);   
    }
  }

  const handlePumpLinkedDeviceToggle = async (typeCode: number, slotNum: number, IsOn: boolean) => {
    dispatch(setLoading(true));
    try {
       // 请求成功后，更新状态
      setPumpLinkedDeviceStatus(typeCode, slotNum, IsOn);
      if (IsOn) {
        // c) Cleaner 和 Spa 互斥：如果开启的是 Cleaner，则关闭 Spa，并禁止用户修改 Spa（通过 handler gate）
        if (
          typeCode === AuxTypeCode.POOL_PRESSURE_CLEANER ||
          typeCode === AuxTypeCode.POOL_IN_FLOOR_CLEANING
        ) {
          try {
            // 仅对主控 Spa 条目发一次关闭请求
            const primarySpa =
              spaList.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_RETURN) ||
              spaList.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_SUCTION) ||
              spaList.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_EXTRA_1) ||
              spaList.find((s) => s.aux.aux_type_code === AuxTypeCode.SPA_ACTUATOR_EXTRA_2) ||
              spaList[0];
            if (primarySpa) {
              await changeNormalEquipmentStatus(primarySpa.aux.aux_type_code, primarySpa.aux.aux_slot_num, false);
              setSpaStatus(primarySpa.aux.aux_type_code, primarySpa.aux.aux_slot_num, false);
            }
          } catch (err) {
            console.error('Failed to turn off spa when cleaner on:', err);
          }
        }
      }
      await changeNormalEquipmentStatus(typeCode, slotNum, IsOn);

      if (IsOn) {
        // 如果此时主泵未开启，则开启主泵并进入等待流程
        if (!pumpIsOn) {
          setDashboardMainStatus(true);
          setExpectMotoStatus(MotorStatus.MOTOR_STATUS_RUNNING);
          await changeMainPumpStatus(true);
          // 启动状态等待前停止常规轮询，避免并发
          pumpInfoPollTimer.stop();
          pumpStatusTimer.reset();
          pumpStatusTimer.start();
        }
      }
    } catch (error) {
      console.error("Failed to toggle pump linked device:", error);
      setPumpLinkedDeviceStatus(typeCode, slotNum, !IsOn);
    } finally {
      setTimeout(async () => {
        dispatch(setLoading(false));
      }, 3000);   
    }
  };

  const handleOtherDeviceToggle = async (typeCode: number, slotNum: number, IsOn: boolean) => {
    dispatch(setLoading(true));
    try {
       // 请求成功后，更新状态
      setOtherDeviceStatus(typeCode, slotNum, IsOn);
      await changeNormalEquipmentStatus(typeCode, slotNum, IsOn);
    } catch (error) {
      console.error("Failed to toggle pump linked device:", error);
      setOtherDeviceStatus(typeCode, slotNum, !IsOn);
    } finally {
      setTimeout(async () => {
        await fetchManualDevice();
        dispatch(setLoading(false));
      }, 3000);   
    }
  };

  const handleLightColorClick = (device: AuxWithSchedules, lightConfig: any) => {
  
    console.log('Opening light control sidebar with device:', device);
    console.log('Light config:', lightConfig);
    
    setSelectedLightDevice(device);
    setIsLightSidebarOpen(true);
  };

  const handlePumpSpeedChange = async (speed: number) => {
    dispatch(setLoading(true));
    if (speed < 40) {
      speed = 40;
    }
    setPumpAuxSpeed(speed);
    const requestBody = {
      aux_type_code: AuxTypeCode.SPALSHME_PUMP,
      pump_speed: speed,
      aux_slot_num: pumpAux.aux_slot_num,
      aux_switch_state: pumpAux.aux_status ? 1 : 0,
    };
    try {
      await apiService.post<GetPumpInfoResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/setPumpInfo`,
        requestBody
      );
    } catch (error) {
      console.error("Failed to set pump speed:", error);
    } finally {
      setTimeout(async () => {
        await fetchManualDevice()
        dispatch(setLoading(false));
      }, 1000);   
    }
  };
  

  const handleColorSelect = async (
    device: AuxWithSchedules,
    colorValue: number
  ) => {
    dispatch(setLoading(true));
    console.log("handleColorSelect", device, colorValue);
    const effectiveLightTypeMode =
      (device.aux.aux_type_code === AuxTypeCode.PAL_LIGHTING ||
        device.aux.aux_type_code === AuxTypeCode.SPA_PAL_LIGHTING) &&
      (device.lightTypeMode == null || device.lightTypeMode === LightTypeMode.LIGHT_UNKNOWN)
        ? LightTypeMode.PAL_COLOUR_TOUCH
        : device.lightTypeMode;
    const requestBody = {
      slot_num: device.aux.aux_slot_num,
      light_type_mode: effectiveLightTypeMode,
      light_colour: colorValue,
    };
    try {
      await apiService.post<GetPumpInfoResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/poolLightSet`,
        requestBody
      );
    } catch (error) {
      console.error("Failed to set pool light color:", error);
    } finally {
      setTimeout(async () => {
        await fetchPoolLightSwitchMode();
        dispatch(setLoading(false));
      }, 1000);   
    }
  };

  const changeMainPumpStatus = async (isOn: boolean) => {
       var speed = pumpAux.pump_speed;
       if (speed < 40) {
         speed = 40;
       }
       const requestBody = {
         aux_switch_state: isOn ? 1 : 0,
         aux_type_code: AuxTypeCode.SPALSHME_PUMP,
         pump_speed: speed,
         aux_slot_num: pumpAux.aux_slot_num,
       };

       await apiService.post<GetPumpInfoResponse>(
         `/api/v1/iot-shadow/device/${selectedDeviceId}/setPumpInfo`,
         requestBody
       );
  }

  const changeNormalEquipmentStatus = async (typeCode: number, slotNum: number, isOn: boolean) => {
    const requestBody = {
         aux_switch_state: isOn ? 1 : 0,
         aux_type_code: typeCode,
         aux_slot_num: slotNum,
       };
       await apiService.post<GetPumpInfoResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/manualSet`,
        requestBody
      );
  }

  const setPumpAuxSpeed = (speed: number) => {
    setPumpAux(prevAux => ({
      ...prevAux,
      pump_speed: speed,
    }));
  }

  const setDashboardMainStatus = (isOn: boolean) => {
    setDashboard((prevDashboard) => ({
      ...prevDashboard,
      main_pump_status: isOn ? 1 : 0,
    }));
  }

  const setPumpLinkedDeviceStatus = (typeCode: number, slotNum: number, isOn: boolean) => {
      setPumpLinkedDeviceList(prevList => 
        prevList.map(device => 
          device.aux_type_code === typeCode && device.aux_slot_num === slotNum
            ? { ...device, aux_status: isOn }
            : device
        )
      );
  }

   const setSpaStatus = (typeCode: number, slotNum: number, isOn: boolean) => {
      setSpaList(prevList => {
        const updatedList = prevList.map(device => {
          const isTarget = device.aux.aux_type_code === typeCode && device.aux.aux_slot_num === slotNum;
          if (isTarget) {
            return { ...device, aux: { ...device.aux, aux_status: isOn } };  
          }
          return device;
        });
        return updatedList;
      });
  }

  const setSpaDeviceStatus = (typeCode: number, slotNum: number, isOn: boolean) => {
      setSpaDevices(prevList => {
        const updatedList = prevList.map(device => {
          const isTarget = device.aux.aux_type_code === typeCode && device.aux.aux_slot_num === slotNum;
          if (isTarget) {
            return { ...device, aux: { ...device.aux, aux_status: isOn } };  
          }
          return device;
        });
        return updatedList;
      });
  }

const setOtherDeviceStatus = (typeCode: number, slotNum: number, isOn: boolean) => {
    setDevicesList(prevList => {
      const updatedList = prevList.map(device => {
        const isTarget = device.aux.aux_type_code === typeCode && device.aux.aux_slot_num === slotNum;
        if (isTarget) {
          return { ...device, aux: { ...device.aux, aux_status: isOn } };  
        }
        return device;
      });
      return updatedList;
    });
  }

  const fetchPumpInfo = async () => {
    try {
      const response = await apiService.get<GetPumpInfoResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/pumpInfo`
      );
      console.log("fetchPumpInfo response:", response);
      setPumpInfo(prevPumpInfo => ({
        ...prevPumpInfo,
        ...response.pump
      }));
    } catch (error) {
      console.error('Failed to fetch pump info:', error);
    }
  };

  const fetchPoolLightSwitchMode = async () => {
    try {
      const response = await apiService.get<GetPoolLightSwitchModeResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/poolLight`
      );
      console.log("fetchPoolLightSwitchMode response:", response);
      setPoolLightSwitchMode(response.poolLight);
    } catch (error) {
      console.error('Failed to fetch pool light switch mode:', error);
    }
  };

  const mergeAuxTimerSnapshot = (manual: Manual) => {
    const pumpTimerAux =
      manual.aux.find((aux) => aux.aux_type_code === AuxTypeCode.SPALSHME_PUMP) ||
      manual.pump_devices.find((aux) => aux.aux_type_code === AuxTypeCode.SPALSHME_PUMP);

    // This aux poll intentionally updates timer fields only.
    // Do not merge aux_status or other switch-related fields here:
    // while Equipment toggles are in flight, the page applies optimistic switch state,
    // and a concurrent aux poll can overwrite that state with stale hardware values.
    // If broader aux synchronization is needed in the future, revisit polling and toggle coordination together.
    setPumpAux((prevPumpAux) => mergeAuxTimerFields(prevPumpAux, pumpTimerAux));
    setPumpLinkedDeviceList((prevList) =>
      mergeAuxListTimerFields(prevList, manual.pump_linked_devices || [])
    );
    setSpaList((prevList) => mergeAuxWithSchedulesTimerFields(prevList, manual.spa || []));
    setSpaDevices((prevList) => mergeAuxWithSchedulesTimerFields(prevList, manual.spa_devices || []));
    setDevicesList((prevList) => mergeAuxWithSchedulesTimerFields(prevList, manual.other_devices || []));
  };

  const fetchAuxTimerInfo = async () => {
    if (!selectedDeviceId) return;

    try {
      const response = await apiService.get<GetAuxInfoResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/auxInfo`
      );
      mergeAuxTimerSnapshot(response.manual);
    } catch (error) {
      console.error('Failed to fetch aux timer info:', error);
    }
  };

  const fetchManualDevice = async () => {
    if (!selectedDeviceId) return;
    
    try {
      const response = await apiService.get<ManualResponse>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/manual`
      );

      // 找到并更新 pump aux
      const newPumpAux = response.manual.aux.find(aux => aux.aux_type_code === AuxTypeCode.SPALSHME_PUMP);
      setPumpAux(newPumpAux || {} as Aux);

      // 更新 pump linked devices
      setPumpLinkedDeviceList(response.manual.pump_linked_devices || []);

      // 处理 spa 设备（防御性判空，保持与 loadDeviceData 一致）
      const spa = Array.isArray(response.manual?.spa) ? response.manual.spa : [];
      const spaDevicesRaw = Array.isArray(response.manual?.spa_devices) ? response.manual.spa_devices : [];
      const spaWithSchedules = spa.map(spa => {
        const deviceSchedules = scheduleInfo?.schedules.filter(
          schedule => schedule.aux_type_code === spa.aux_type_code
        ) || [];
        return {
          aux: spa,
          schedules: deviceSchedules
        };
      });

      // 处理 spa devices
      const spaDevicesWithSchedules = spaDevicesRaw.map(spa => {
        const deviceSchedules = scheduleInfo?.schedules.filter(
          schedule => schedule.aux_type_code === spa.aux_type_code
        ) || [];
        return {
          aux: spa,
          schedules: deviceSchedules
        };
      });

      // 处理其他设备（防御性判空）
      const otherDevices = Array.isArray(response.manual?.other_devices) ? response.manual.other_devices : [];
      const otherDevicesWithSchedules = otherDevices.map(other => {
        const deviceSchedules = scheduleInfo?.schedules.filter(
          schedule => schedule.aux_type_code === other.aux_type_code
        ) || [];
        return {
          aux: other,
          schedules: deviceSchedules
        };
      });

      // 更新所有设备列表
      setSpaList(spaWithSchedules);
      setSpaDevices(spaDevicesWithSchedules);
      setDevicesList(otherDevicesWithSchedules);

    } catch (error) {
      console.error('Failed to fetch manual device:', error);
    }
  };

  return (
    <>
     {/* <GlobalLoading active={loading} /> */}
    <div className="page-container relative flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 ">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FiSliders className="text-primary-500 text-xl" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                My Equipment
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Control and monitor all your pool equipment
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DeviceSelector
              onDeviceChange={onDeviceChange}
              selectedDeviceId={selectedDeviceId}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="h-0.5 bg-gray-200 dark:bg-gray-700 my-4"></div>

        {/* Main Pump Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <MainPumpSection
            pump={pumpInfo}
            scheduleInfo={scheduleInfo}
            pumpLinkedDeviceList={pumpLinkedDeviceList}
            dashboard={dashboard}
            pumpAux={pumpAux}
            setSpeedUI={setSpeedUI}
            onPumpToggle={handlePumpToggle}
            onPumpSpeedChange={handlePumpSpeedChange}
            onPumpLinkedDeviceToggle={handlePumpLinkedDeviceToggle}
            // 传递互斥状态：Spa 开启时，Cleaner 开关替换为禁止图标
            isSpaOn={isSpaOn}
            expectMotoStatus={expectMotoStatus}
          />
        </div>

        {/* Spa Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <SpaSection
            spaList={spaList}
            spaDevices={spaDevices}
            scheduleInfo={scheduleInfo}
            onSpaToggle={handleSpaToggle}
            onSpaDeviceToggle={handleSpaDeviceToggle}
            // 传递互斥状态：Cleaner 开启时，Spa 开关替换为禁止图标
            isCleanerOn={isCleanerOn}
            isHeaterCoolDown={Boolean(pumpInfo?.pump_cooldown)}
          />
        </div>

        {/* Other Devices Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <OtherDevicesSection
            devicesList={devicesList}
            scheduleInfo={scheduleInfo}
            onDeviceToggle={handleOtherDeviceToggle}
            poolLightSwitchMode={poolLightSwitchMode}
            onLightColorClick={handleLightColorClick}
          />
        </div>

        {hasRs485 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">RS485</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Mineral Swim V2</p>
              </div>
              <span className="inline-flex items-center rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700 dark:bg-teal-900/20 dark:text-teal-300">
                Assigned
              </span>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
              RS485 sanitisation hardware is installed on this system. Live chlorinator and ozone details are available in Chemistry.
            </p>
          </div>
        )}
      </div>

      {/* Light Control Sidebar */}
      <LightControlSidebar
        isOpen={isLightSidebarOpen}
        onClose={() => setIsLightSidebarOpen(false)}
        device={selectedLightDevice}
        onColorSelect={handleColorSelect}
      />
    </div>
    </>
  );
};

export default DevicesPage;
